import { DISPLAY_EPSILON } from './constants';
import { helpText, Help } from './help';
import { formatDate, formatMetric, displayDeviceLabel } from './format';
import { isMissing, usualRange, runsBefore, sameDevices } from './metrics';
import { StatusBadge } from './KpiCards';

const VISIBLE_RUN_COUNT = 3;

// The card figure that moved the most against the run before, in multiples of
// its normal night-to-night variation (half the usual range).
function biggestChange(run, index, reportedRuns, summaries, kpis) {
  const previous = reportedRuns[index + 1];
  if (!previous) return null;
  const earlier = runsBefore(reportedRuns, run);
  const summary = summaries.get(run.id);
  const scored = kpis.map((kpi) => {
    if (!sameDevices(kpi, summary, summaries.get(previous.id))) return null;
    const value = kpi.read(summary);
    const before = kpi.read(summaries.get(previous.id));
    const range = usualRange(earlier
      .filter((other) => sameDevices(kpi, summary, summaries.get(other.id)))
      .map((other) => kpi.read(summaries.get(other.id))));
    if (isMissing(value) || isMissing(before) || isMissing(range?.low)) return null;
    const variation = (range.high - range.low) / 2;
    const change = value - before;
    const score = variation > 0 ? Math.abs(change) / variation : (Math.abs(change) > DISPLAY_EPSILON ? Infinity : 0);
    return { kpi, change, score };
  }).filter(Boolean);
  const top = scored.reduce((best, item) => (!best || item.score > best.score ? item : best), null);
  if (!top || top.score <= 1) return null;
  const phrase = top.kpi.phrase.charAt(0).toUpperCase() + top.kpi.phrase.slice(1);
  return `${phrase} ${formatMetric(top.change, top.kpi.unit, true)}`;
}

export function RunHistory({ reportedRuns, summaries, kpis, platform }) {
  const visibleRuns = reportedRuns.slice(0, VISIBLE_RUN_COUNT);
  return (
    <table className="sdkp-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Result</th>
          <th>SDK</th>
          <th>Test app</th>
          <th>Devices</th>
          <th>
            Biggest change vs previous run
            <Help text={helpText('biggest', platform)} placement="below" align="right" />
          </th>
          <th aria-label="Run link" />
        </tr>
      </thead>
      <tbody>
        {visibleRuns.map((run, index) => {
          const summary = summaries.get(run.id);
          return (
            <tr key={run.id}>
              <td>{formatDate(run.uploadedAt)}</td>
              <td><StatusBadge conclusion={run.conclusion} /></td>
              <td>{summary?.sdkVersion || '-'}</td>
              <td>{summary?.appVersion || '-'}</td>
              <td title={(summary?.devices || []).map(displayDeviceLabel).join('\n')}>{summary?.devices?.length || '-'}</td>
              <td>{biggestChange(run, index, reportedRuns, summaries, kpis) || '—'}</td>
              <td>{run.url ? <a href={run.url} target="_blank" rel="noopener noreferrer">Open ↗</a> : '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
